import { getApiErrorMessage } from '@components/common/util/util';
import { apiAuthCaller } from '@services/apiCaller';
import { AxiosResponse } from 'axios';
import { action, observable } from 'mobx';
import { IRootStore } from '.';

type Permission = { locked: boolean; permissionId: string | undefined };
export type Resource = 'source' | 'destination' | 'transformation' | 'model' | 'workspace';

export interface IPermissions {
  resources: undefined | Record<string, Permission>;
  hasPermission(resourceId: string): boolean;
  registerResource(
    items: { id: string; permissionId: string | undefined; locked: boolean | undefined }[],
  ): void;
  createPermission(resourceId: string, users: string[], resourceType: Resource): Promise<boolean>;
  updateResourceAccess(
    resourceId: string,
    permissionId: string,
    isLocked: boolean,
    users: string[],
    resourceType: Resource,
  ): Promise<boolean>;
  addNewResource(id: string): void;

  // PII permissions
  hasDataPermission(): boolean;
  setDataPermissions(payload: { id: string; isLocked: boolean }): void;
  dataPermissions: undefined | Permission;
}

export class PermissionsStore implements IPermissions {
  rootStore: IRootStore;

  @observable resources: undefined | Record<string, Permission>;

  @observable dataPermissions: undefined | Permission;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action.bound
  hasPermission(resourceId: string) {
    const { isAdmin } = this.rootStore.userStore;
    if (!isAdmin || !this.resources) {
      return false;
    }
    const resource = this.resources[resourceId];
    if (!resource) {
      return false;
    }
    return !resource.locked;
  }

  registerResource(
    items: { id: string; permissionId: string | undefined; locked: boolean | undefined }[],
  ) {
    this.resources = items.reduce(
      (acc, elem) => ({
        ...acc,
        [elem.id]: {
          locked: elem.locked || false,
          permissionId: elem.permissionId || '',
        },
      }),
      this.resources,
    );
  }

  createPermission(resourceId: string, users: string[], resourceType: Resource) {
    const {
      userStore: { token, id: userId },
      messagesStore,
    } = this.rootStore;
    return apiAuthCaller(token)
      .post('/permissions', { locked: true, users, resourceId, resourceType })
      .then((res: AxiosResponse<{ permissionId: string }>) => {
        const result = {
          permissionId: res.data.permissionId,
          locked: !users.includes(userId),
        };
        if (resourceType === 'workspace') {
          this.dataPermissions = result;
        } else {
          this.resources![resourceId] = result;
        }
        return true;
      })
      .catch((err) => {
        messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to lock the resource'));
        return false;
      });
  }

  updateResourceAccess(
    resourceId: string,
    permissionId: string,
    isLocked: boolean,
    users: string[],
    resourceType: Resource,
  ) {
    const {
      userStore: { id: userId, token },
      messagesStore,
    } = this.rootStore;
    const resource = this.resources?.[resourceId];
    return apiAuthCaller(token)
      .patch(`/permissions/${permissionId}`, { locked: isLocked, users })
      .then(() => {
        const locked = isLocked && !users.includes(userId);
        if (resourceType === 'workspace') {
          this.dataPermissions = { ...this.dataPermissions!, locked };
        } else {
          this.resources![resourceId] = { ...resource!, locked };
        }
        return true;
      })
      .catch((err) => {
        messagesStore.showErrorMessage(
          getApiErrorMessage(err, 'Failed to update resource permissions'),
        );
        return false;
      });
  }

  @action.bound
  hasDataPermission() {
    if (!this.dataPermissions) {
      return false;
    }
    return !this.dataPermissions.locked;
  }

  setDataPermissions(payload: { id: string; isLocked: boolean }) {
    const { isLocked = false, id = '' } = payload || {};
    this.dataPermissions = { locked: isLocked, permissionId: id };
  }

  addNewResource(resourceId: string) {
    this.registerResource([{ id: resourceId, permissionId: '', locked: false }]);
  }
}
